import type { Money, Source, SourceEntity } from "../api/types";

/**
 * The other account a transaction faces, when that account is one of your own.
 *
 * This is *derived*, not stored. A transaction already carries `payToAccount`
 * (the bank's counter-account number), and the source registry now carries
 * each account's own `accountNumber`, so the counterparty is whichever source
 * those two agree on. Nothing needs to be written, and the answer can never
 * drift out of step with the registry.
 *
 * A number that matches no source is an external payee — a shop, a utility,
 * another person — and resolves to `undefined`, which is the honest answer:
 * they are not one of your accounts, and the merchant already names them.
 */

/**
 * Account numbers are compared on digits alone.
 *
 * Norwegian numbers are written `1225.88.74453` in some exports and
 * `1225 88 74453` in others — the DNB Mastercard invoice detector even has a
 * `spacesToDots` transform for exactly this. Comparing the raw strings would
 * make a hand-typed registry entry silently fail to match an imported row.
 */
export const normalizeAccountNumber = (value: string): string =>
  value.replace(/[^0-9a-zA-Z]/g, "");

/** A source paired with the normalized number it answers to. */
export type AccountNumberIndex = Map<string, SourceEntity>;

/**
 * Index the sources that have an account number, keyed by its normalized form.
 *
 * Two sources carrying the same number is a data error nothing currently
 * prevents. The first wins and the collision is reported rather than silently
 * resolving to whichever happened to be iterated last — a wrong counterparty
 * is worse than none, because it reads as a fact about where money went.
 */
export const buildAccountNumberIndex = (
  sources: (SourceEntity | undefined)[],
  onCollision?: (number: string, kept: Source, dropped: Source) => void,
): AccountNumberIndex => {
  const index: AccountNumberIndex = new Map();
  for (const source of sources) {
    if (!source?.accountNumber) continue;
    const key = normalizeAccountNumber(source.accountNumber);
    if (!key) continue;
    const existing = index.get(key);
    if (existing) {
      onCollision?.(source.accountNumber, existing.name, source.name);
      continue;
    }
    index.set(key, source);
  }
  return index;
};

/** The own-account this `payToAccount` names, or `undefined` if external. */
export const resolveCounterparty = (
  payToAccount: string | null | undefined,
  index: AccountNumberIndex,
): SourceEntity | undefined => {
  if (!payToAccount) return undefined;
  const key = normalizeAccountNumber(payToAccount);
  if (!key) return undefined;
  return index.get(key);
};

export type TransferDirection = {
  /** Where the money came from. */
  from: string;
  /** Where it went. */
  to: string;
  /** True when both ends are your own accounts — an internal transfer. */
  internal: boolean;
};

/**
 * Read a transaction as a movement, `from → to`.
 *
 * Direction comes from the sign of the amount, which is the only thing that
 * carries it: `source` names the ledger the row was booked on, not the origin
 * of the money. A negative amount left that account; a positive one arrived.
 * So the same stored shape reads correctly in both directions without storing
 * a direction anywhere.
 *
 * The far end is the counterparty when it is one of your accounts, and the
 * merchant otherwise — which is why an external payee needs no registry entry.
 */
export const describeTransfer = (
  amount: Money,
  source: Source,
  merchant: string,
  counterparty: SourceEntity | undefined,
): TransferDirection => {
  const far = counterparty?.name ?? merchant;
  const outgoing = Number(amount.amount) < 0;
  return {
    from: outgoing ? source : far,
    to: outgoing ? far : source,
    internal: counterparty !== undefined,
  };
};

export type CounterpartyMarker = {
  /** Points away from this row's account when money left it. */
  arrow: "→" | "←";
  name: Source;
};

/**
 * The counterparty as a one-glance marker for a list row.
 *
 * The arrow is not decoration: on the incoming leg of a transfer the far end
 * is where the money *came from*, so a bare `→` would read backwards. The sign
 * of the amount is what tells the two legs apart.
 */
export const counterpartyMarker = (
  amount: Money,
  counterparty: SourceEntity | undefined,
): CounterpartyMarker | undefined => {
  if (!counterparty) return undefined;
  return {
    arrow: Number(amount.amount) < 0 ? "→" : "←",
    name: counterparty.name,
  };
};
